<template>
  <v-app pb-3>
    <v-content>
      <v-form ref="form" v-model="isFormValid">
        <!-- ── Шапка полиса ──────────────────────────────────────────────── -->
        <policy-header :policy="orderData.policy" :insurer="orderData.insurer" />

        <!-- ── Карточки водителей ────────────────────────────────────────── -->
        <driver-card
          v-for="(driver, index) in drivers"
          :key="driver.n"
          :driver="driver"
          :dateToday="dateToday"
          :dateDriversMinimal="dateDriversMinimal"
          :canAddDriver="drivers.length < 4 && driver.n === drivers.length"
          @add-driver="addDriver"
          @remove-driver="removeDriver"
          @paste-license="handlePasteDriverLicense($event.event, $event.driver)"
          @update:license="onUpdateDriverLicense"
          @update:licenseNew="onUpdateDriverLicenseNew"
          @update:lastnameNew="onUpdateLastnameNew"
          @update:field="onUpdateDriverField"
          @birthdate-change="onDriverBirthdateChange"
        />

        <!-- ── Секция транспортного средства ─────────────────────────────── -->
        <vehicle-document-section
          :documents="orderData.vehicle.documents"
          :vehicleDocMask="vehicleDocMask"
          :dateToday="dateToday"
          @update:registrationNumberNew="
            orderData.vehicle.documents.registrationNumberNew = $event;
            markFormChanged();
          "
          @update:typeNew="
            orderData.vehicle.documents.typeNew = $event;
            markFormChanged();
          "
          @update:seriesNumberNew="
            orderData.vehicle.documents.seriesNumberNew = $event;
            markFormChanged();
          "
          @update:issueDateNew="
            orderData.vehicle.documents.issueDateNew = $event;
            markFormChanged();
          "
          @update-mask="updateVehicleDocMask"
          @paste-doc="handlePasteVehicleDoc"
        />

        <!-- ── Кнопка расчёта и согласие ─────────────────────────────────── -->
        <v-container style="max-width: 1240px">
          <v-layout row wrap pt-4>
            <v-flex xs12 sm6>
              <v-btn
                class="submit-button"
                :loading="wait"
                :disabled="isPayed"
                @click="validate"
              >
                Рассчитать окончательную стоимость
              </v-btn>
            </v-flex>
            <v-flex xs12 sm6>
              <div class="policy-consent">
                Отправляя анкету, соглашаюсь с
                <a
                  class="policy-consent__link"
                  target="_blank"
                  href="https://getpolis.ru/osago_policy"
                >политикой обработки персональных данных</a>
                и даю<a
                  class="policy-consent__link"
                  target="_blank"
                  href="https://getpolis.ru/soglasie_pdn"
                >Согласие на обработку персональных данных</a>
              </div>
            </v-flex>
          </v-layout>

          <!-- ── Предложения страховых компаний ──────────────────────────── -->
          <template v-if="companiesList.length">
            <company-offer
              v-for="(company, index) in companiesList"
              :key="index"
              :company="company"
              :buttonText="getMainButtonText(company)"
              :isButtonDisabled="isMainButtonDisabled(company)"
              :isButtonLoading="isMainButtonLoading"
              @action-click="handleMainButtonClick(company)"
            />
          </template>
        </v-container>
      </v-form>
    </v-content>
  </v-app>
</template>

<script>
import moment from 'moment';
import PolicyHeader from './components/PolicyHeader.vue';
import DriverCard from './components/DriverCard.vue';
import VehicleDocumentSection from './components/VehicleDocumentSection.vue';
import CompanyOffer from './components/CompanyOffer.vue';
import { useDriverLicenseMask } from './composables/useDriverLicenseMask';

export default {
  name: 'ChangeContainer',

  components: {
    PolicyHeader,
    DriverCard,
    VehicleDocumentSection,
    CompanyOffer,
  },

  props: {
    order: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      isFormValid: true,

      PDF_POLL_MAX_ATTEMPTS: 30,
      PDF_POLL_INITIAL_DELAY: 5000,
      PDF_POLL_MAX_DELAY: 30000,

      dateToday: moment().format('YYYY-MM-DD'),
      dateDriversMinimal: moment().subtract(18, 'years').format('YYYY-MM-DD'),

      drivers: [],
      companiesList: [],

      orderData: {
        policy: {},
        insurer: {},
        vehicle: {
          documents: {
            issueDate: null,
            issueDateNew: null,
            registrationNumber: null,
            registrationNumberNew: null,
            seriesNumber: null,
            seriesNumberNew: null,
            type: null,
            typeNew: null,
          },
        },
      },

      vehicleDocMask: 'S',

      uuid: null,
      wait: false,
      isPayed: false,
      isFormChanged: false,
      isOrderDataLoaded: false,

      applyResult: null,
      pdfStatus: 'idle', // idle | loading | ready | timeout | error | requiresPayment
      pdfUrl: null,
      pollMessage: '',

      pdfPollingTimer: null,
      pdfPollingAttempts: 0,
      preparedFormData: null,
    };
  },

  computed: {
    isMainButtonLoading() {
      return this.wait || this.pdfStatus === 'loading';
    },
  },

  created() {
    const maskApi = useDriverLicenseMask();
    this.resolveMask = maskApi.resolveMask;
    this.formatPastedLicenseNumber = maskApi.formatPastedLicenseNumber;

    this.initFromOrder();
  },

  beforeDestroy() {
    clearTimeout(this.pdfPollingTimer);
  },

  updated() {
    if (window.tippy && window.tippy.default) {
      window.tippy.default('.tip-icon');
    }
  },

  methods: {
    initFromOrder() {
      const order = JSON.parse(window.atob(this.order));
      if (!order) return;

      if (order.drivers && Array.isArray(order.drivers)) {
        this.drivers = order.drivers.map((driver, index) => this._normalizeDriver(driver, index));
      }

      if (order.policy) {
        this.orderData.policy = order.policy;
        if (order.policy.id) this.uuid = order.policy.id;
      }

      if (order.insurer) {
        this.orderData.insurer = order.insurer;
      }

      if (order && order.vehicle && order.vehicle.documents) {
        Object.assign(this.orderData.vehicle.documents, order.vehicle.documents);
        if (order.vehicle.documents.type) {
          this.orderData.vehicle.documents.typeNew = order.vehicle.documents.type;
        }
      }

      if (order.draft) {
        this.companiesList = [order.draft];
      } else {
        this.companiesList = [];
      }

      this._updateVehicleDocMask();

      setTimeout(() => {
        this.isOrderDataLoaded = true;
      }, 1000);
    },

    _normalizeDriver(driver, index) {
      const normalized = {
        ...driver,
        n: driver.n != null ? driver.n : index + 1,
        isFromBack: driver.isFromBack != null ? driver.isFromBack : true,
      };

      if (!normalized.licenseMask) {
        const license = normalized.license;
        const licenseValue = (license && license.seriesNumberNew) || (license && license.seriesNumber);
        normalized.licenseMask = licenseValue ? this.resolveMask(licenseValue) : null;
      }

      return normalized;
    },

    addDriver() {
      if (this.drivers.length >= 4) return;

      const n = this.drivers.length + 1;
      this.drivers.push({
        id: String(n),
        n,
        isFromBack: false,
        lastname: '',
        firstname: '',
        middlename: '',
        noMiddlename: false,
        birthdate: null,
        gender: null,
        license: { seriesNumber: null, issueDate: null },
        licenseMask: null,
        dateFirstLicense: this.dateDriversMinimal,
        dateDriverPassport: this.dateDriversMinimal,
      });

      this.markFormChanged();
    },

    removeDriver(driver) {
      if (driver.isOwner || driver.isInsurer) return;

      const index = this.drivers.findIndex((d) => d.n === driver.n);
      if (index === -1) return;

      this.drivers.splice(index, 1);
      this.drivers.forEach((d, i) => { d.n = i + 1; });

      this.markFormChanged();
    },

    recalcDriverDates(driver) {
      if (!driver.birthdate) return;

      const [year, month, day] = driver.birthdate.split('-');
      driver.dateFirstLicense = `${+year + 18}-${month}-${day}`;
      driver.dateDriverPassport = `${+year + 14}-${month}-${day}`;

      if (driver.license && driver.license.date) {
        if (driver.license.date > this.dateToday) {
          driver.license.date = this.dateToday;
        } else if (driver.license.date < driver.dateFirstLicense) {
          driver.license.date = driver.dateFirstLicense;
        }
      }
    },

    updateDriverLicenseMask(driver, value) {
      driver.licenseMask = this.resolveMask(value) || 'S';
    },

    handlePasteDriverLicense(event, driver) {
      const raw = (event.clipboardData || window.clipboardData).getData('text').trim();
      if (!raw) return;

      driver.licenseMask = this.resolveMask(raw);
      const formatted = this.formatPastedLicenseNumber(raw);

      this.$nextTick(() => {
        if (driver.isFromBack) {
          this.$set(driver.license, 'seriesNumberNew', formatted);
        } else {
          this.$set(driver.license, 'seriesNumber', formatted);
        }
      });

      event.preventDefault();
    },

    _updateVehicleDocMask() {
      const value =
        this.orderData.vehicle.documents.seriesNumber != null
          ? this.orderData.vehicle.documents.seriesNumber
          : '';
      this.vehicleDocMask = this.resolveMask(value);
    },

    updateVehicleDocMask() {
      this._updateVehicleDocMask();
    },

    handlePasteVehicleDoc(event) {
      const raw = event.clipboardData.getData('text').trim();
      if (!raw) return;

      this.vehicleDocMask = this.resolveMask(raw);

      this.$nextTick(() => {
        this.orderData.vehicle.documents.seriesNumber = raw;
      });

      event.preventDefault();
    },

    markFormChanged() {
      if (this.isOrderDataLoaded) this.isFormChanged = true;
    },

    _prepareFormData() {
      const vehicleCopy = JSON.parse(JSON.stringify(this.orderData.vehicle));

      if (vehicleCopy.documents) {
        const newFields = ['registrationNumber', 'seriesNumber', 'issueDate', 'type'];
        for (const field of newFields) {
          const key = `${field}New`;
          const val = vehicleCopy.documents[key];
          if (val === undefined || val === null || val === '') {
            delete vehicleCopy.documents[key];
          }
        }
      }

      this.preparedFormData = {
        insurer: this.orderData.insurer,
        policy: this.orderData.policy,
        drivers: JSON.parse(JSON.stringify(this.drivers)),
        vehicle: vehicleCopy,
      };
    },

    async validate() {
      if (!this.$refs.form.validate()) {
        PNotify.error('Проверьте правильность выделенных полей.');
        window.$vuetify && window.$vuetify.goTo && window.$vuetify.goTo('.error--text', {
          duration: 600,
          offset: 150,
          easing: 'easeInCubic',
        });
        return;
      }

      this._prepareFormData();

      try {
        const response = await this._saveDraft(this.preparedFormData);

        if (response && response.data && response.data.success) {
          await this._submit();
        } else {
          PNotify.error('Не удалось сохранить черновик. Попробуйте снова.');
        }
      } catch (error) {
        const message =
          (error.response &&
            error.response.data &&
            error.response.data.error) != null
            ? error.response.data.error
            : 'Произошла ошибка при сохранении черновика.';

        PNotify.error(message);
      }
    },

    _saveDraft(formData) {
      return axios.post('change/draft', {
        applyChanges: 0,
        cause: 'bb67e2c3-a8b7-11e5-8106-0cc47a0f017e',
        changes: formData,
      });
    },

    async _submit() {
      this.isFormChanged = false;
      this.wait = true;

      try {
        const response = await axios.post('change/calc');
        const data = response.data;
        this.companiesList = data && data.raw ? [data] : [];
      } catch (error) {
        console.error('Ошибка при расчёте:', error);
      } finally {
        this.wait = false;
      }
    },

    async apply() {
      this.wait = true;
      this.pdfStatus = 'idle';
      this.pdfUrl = null;
      this.pdfPollingAttempts = 0;
      clearTimeout(this.pdfPollingTimer);

      try {
        const response = await axios.post('change/apply');
        const data = response.data;

        if (data && data.response && data.response.result) {
          PNotify.success('Изменения применены. Полис формируется...');
          this.applyResult = data;
          this.pdfStatus = 'loading';

          if (data.pdfUrl) {
            this.pdfUrl = data.pdfUrl;
            this.pdfStatus = 'ready';
          } else {
            const changeNumber = data.changeNumber != null ? data.changeNumber : 1;
            this._startPdfPolling(data.changeId, changeNumber);
          }
        } else {
          const errorMessage =
            (data && data.error) != null
              ? data.error
              : 'Не удалось применить изменения';

          PNotify.error(errorMessage);
        }
      } catch (error) {
        const err =
          error.response &&
          error.response.data &&
          error.response.data.error;

        const msg = err != null ? err : 'Ошибка при применении изменений';
        PNotify.error(msg);
      } finally {
        this.wait = false;
      }
    },

    _startPdfPolling(changeId, changeNumber, delay = this.PDF_POLL_INITIAL_DELAY) {
      this.pdfPollingTimer = setTimeout(async () => {
        this.pdfPollingAttempts++;

        if (this.pdfPollingAttempts > this.PDF_POLL_MAX_ATTEMPTS) {
          this.pdfStatus = 'timeout';
          return;
        }

        try {
          const { data } = await axios.get(`change/${changeNumber}/pdf-status`);

          this.pollMessage =
            data.userMessage != null
              ? data.userMessage
              : 'Ожидание ответа от страховой...';

          if (data.requiresPayment) {
            this.pdfStatus = 'requiresPayment';
            return;
          }

          if (data.ready && data.pdfUrl) {
            this.pdfUrl = data.pdfUrl;
            this.pdfStatus = 'ready';
            PNotify.success('Полис сформирован, можно скачать');
            return;
          }
        } catch (e) {
          // Продолжаем поллинг даже при сетевой ошибке
        }

        const nextDelay = Math.min(delay * 1.5, this.PDF_POLL_MAX_DELAY);
        this._startPdfPolling(changeId, changeNumber, nextDelay);
      }, delay);
    },

    retryPdf() {
      if (!this.applyResult) return;
      const changeNumber =
        this.applyResult.changeNumber != null
          ? this.applyResult.changeNumber
          : 1;
      this.pdfStatus = 'loading';
      this.pdfPollingAttempts = 0;
      this._startPdfPolling(this.applyResult.changeId, changeNumber);
    },

    getMainButtonText(company) {
      if (this.isFormChanged) return 'Проведите перерасчет';

      const statusLabels = {
        ready: 'Скачать полис',
        loading: 'Полис формируется...',
        timeout: 'Повторить запрос',
        requiresPayment: 'Требуется доплата',
      };

      if (statusLabels[this.pdfStatus]) return statusLabels[this.pdfStatus];

      return this._formatApplyButtonLabel(company.extraPayment, company.issueType);
    },

    _formatApplyButtonLabel(extraPayment, issueType) {
      if (issueType === 'refund') return `Возврат ${Math.abs(extraPayment)}₽`;
      if (issueType === 'payment') return `Оформить за ${extraPayment}₽`;
      return 'Оформить';
    },

    isMainButtonDisabled(company) {
      return (
        this.isFormChanged ||
        !company.allowIssue ||
        this.pdfStatus === 'loading' ||
        this.pdfStatus === 'requiresPayment'
      );
    },

    handleMainButtonClick(company) {
      if (this.isFormChanged) return;

      switch (this.pdfStatus) {
        case 'ready':
          window.open(this.pdfUrl, '_blank');
          break;
        case 'timeout':
        case 'error':
          this.retryPdf();
          break;
        default:
          this.apply();
      }
    },

    // ── Хэндлеры событий дочерних компонентов ────────────────────────────
    onUpdateDriverLicense({ driver, value }) {
      this.$set(driver.license, 'seriesNumber', value);
      this.updateDriverLicenseMask(driver, value);
      this.markFormChanged();
    },

    onUpdateDriverLicenseNew({ driver, value }) {
      this.$set(driver.license, 'seriesNumberNew', value);
      this.updateDriverLicenseMask(driver, value);
      this.markFormChanged();
    },

    onUpdateLastnameNew({ driver, value }) {
      this.$set(driver, 'lastnameNew', value);
      this.markFormChanged();
    },

    onUpdateDriverField({ driver, field, value }) {
      const keys = field.split('.');
      let obj = driver;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!obj[keys[i]]) this.$set(obj, keys[i], {});
        obj = obj[keys[i]];
      }
      this.$set(obj, keys[keys.length - 1], value);
      this.markFormChanged();
    },

    onDriverBirthdateChange({ driver, value }) {
      this.$set(driver, 'birthdate', value);
      this.recalcDriverDates(driver);
      this.markFormChanged();
    },
  },
};
</script>

<style scoped lang="scss">
.submit-button {
  color: #ffffff !important;
  line-height: 1.45 !important;
  font-weight: 700 !important;
  font-size: 24px !important;
  text-transform: none !important;
  box-shadow: none !important;

  height: 100px;
  background-color: #00ba57 !important;
  text-align: center;
  border-radius: 100px;
  border: 1px solid transparent;
  padding: 0 !important;
  margin: 0;

  >>> .v-btn__content {
    white-space: normal;
    padding: 0 50px;
    line-height: 1.1;
  }
}

.policy-consent {
  height: 100px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  font-size: 14px;

  &__link {
    color: #00ba57 !important;
    text-decoration: underline;
  }
}
</style>



// WEBPACK FOOTER //
// resources/js/components/change/ChangeContainer.vue